import React, { useEffect, useState } from 'react';
import {
  Device,
  FireSuppressionSystem,
  useFireSuppressionSystemDetailQuery,
  useFireSuppressionSystemDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';

interface FireSuppressionSystemProps {
  id: string;
}

export const FireSuppressionSystemComponent: React.FC<
  FireSuppressionSystemProps
> = ({ id }) => {
  const [device, setDevice] = useState<Partial<FireSuppressionSystem>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  const { data } = useFireSuppressionSystemDetailQuery({
    variables: { id },
  });

  useFireSuppressionSystemDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.fireSuppressionSystemById) {
      setDevice(
        data.fireSuppressionSystemById as Partial<FireSuppressionSystem>,
      );
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const info: DeviceInformation[] = [
        {
          'Device Identification': {
            'Device Name': device.name ?? 'Unnamed Fire Suppression System',
            'Device ID': device.deviceId ?? 'Unidentified',
            'Device Type': device.typeDisplayName ?? 'Unknown',
            'Last Updated': moment(device.timestamp).fromNow(),
          },
          'System Status': {
            'Fire Suppression Status':
              device.fireSuppressionStatus?.value ?? 'Unknown',
            'Sensor Status': device.sensorStatus?.value ?? 'Unknown',
            'Suppressant Level': formatDeviceMetric({
              value: device.suppressantLevel?.value ?? undefined,
              units: device.suppressantLevel?.units ?? undefined,
            }),
          },
          'Operational Status': {
            'Power State': device.powerState?.value ? 'On' : 'Off',
            'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
            'Is Configured': device.isConfigured ? 'Yes' : 'No',
            'Is Controlled': device.isControlled ? 'Yes' : 'No',
            'Has Pending Updates': device.hasPendingUpdates ? 'Yes' : 'No',
          },
          'System Integration': {
            Property: device.property?.title ?? 'Not assigned',
            Unit: device.unit?.name ?? 'Not assigned',
            Panel: device.panel?.displayName ?? 'Not assigned',
            Source: device.source?.name ?? 'Not assigned',
          },
        },
      ];
      setDeviceInfo(info);
    }
  }, [device]);

  if (!device) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  return (
    <PanelFrame
      title="Fire Suppression System"
      iconType="mdi:fire-extinguisher"
      device={device as Partial<Device>}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Fire Suppression Status
            </Typography>
            <Typography variant="h6" align="center">
              {device.fireSuppressionStatus?.value ?? 'Unknown'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Sensor Status
            </Typography>
            <Typography variant="h6" align="center">
              {device.sensorStatus?.value ?? 'Unknown'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Suppressant Level
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric({
                value: device.suppressantLevel?.value ?? undefined,
                units: device.suppressantLevel?.units ?? undefined,
              })}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </PanelFrame>
  );
};
